import axios from 'axios'

const API_BASE = process.env.REACT_APP_NEXTERA_API

export type InspectionMlLabelStatus = 'pass' | 'fail' | 'warn'

export interface InspectionMlLabel {
  label: string
  status: InspectionMlLabelStatus
}

export interface InspectionTargetZoneRecord {
  id: number
  name: string
  category: string
  confidence_threshold: number
  ml_labels: InspectionMlLabel[]
  overlay_x: number
  overlay_y: number
  overlay_width: number
  overlay_height: number
  viewpoint_sub_id: number
  display_order: number
}

export interface ViewpointZoneRecord {
  id: number
  name: string
  description: string | null
  viewpoint_sub_ids: number[]
  target_zone_count: number
}

export interface UpsertInspectionTargetZonePayload {
  name: string
  category: string
  confidence_threshold: number
  ml_labels: InspectionMlLabel[]
  overlay_x: number
  overlay_y: number
  overlay_width: number
  overlay_height: number
  viewpoint_sub_id: number
  display_order: number
}

const unwrap = <T>(response: { data: T } | { data: { data: T } }): T => {
  const responseData = response.data as T | { data: T }
  if (
    responseData &&
    typeof responseData === 'object' &&
    'data' in (responseData as { data?: T })
  ) {
    return (responseData as { data: T }).data
  }
  return responseData as T
}

const buildParams = (viewpointZoneId?: number | null) => {
  if (!viewpointZoneId) return undefined
  return {
    viewpoint_zone_id: viewpointZoneId,
  }
}

export const fetchInspectionTargetZones = async (
  projectId: string,
  viewpointZoneId?: number | null,
): Promise<InspectionTargetZoneRecord[]> => {
  const response = await axios.get(
    `${API_BASE}/project/${projectId}/inspection-target-zones`,
    { params: buildParams(viewpointZoneId) },
  )
  return unwrap<InspectionTargetZoneRecord[]>(response)
}

export const fetchInspectionViewpointZones = async (
  projectId: string,
): Promise<ViewpointZoneRecord[]> => {
  const response = await axios.get(
    `${API_BASE}/project/${projectId}/inspection-target-zones/viewpoint-zones`,
  )
  return unwrap<ViewpointZoneRecord[]>(response)
}

export const createInspectionTargetZone = async (
  projectId: string,
  payload: UpsertInspectionTargetZonePayload,
  viewpointZoneId?: number | null,
): Promise<InspectionTargetZoneRecord> => {
  const response = await axios.post(
    `${API_BASE}/project/${projectId}/inspection-target-zone`,
    payload,
    { params: buildParams(viewpointZoneId) },
  )
  return unwrap<InspectionTargetZoneRecord>(response)
}

export const updateInspectionTargetZone = async (
  projectId: string,
  targetZoneId: number,
  payload: Partial<UpsertInspectionTargetZonePayload>,
  viewpointZoneId?: number | null,
): Promise<InspectionTargetZoneRecord> => {
  const response = await axios.patch(
    `${API_BASE}/project/${projectId}/inspection-target-zone/${targetZoneId}`,
    payload,
    { params: buildParams(viewpointZoneId) },
  )
  return unwrap<InspectionTargetZoneRecord>(response)
}

export const deleteInspectionTargetZone = async (
  projectId: string,
  targetZoneId: number,
): Promise<void> => {
  await axios.delete(
    `${API_BASE}/project/${projectId}/inspection-target-zone/${targetZoneId}`,
  )
}
